import firebase from 'firebase/app';
import 'firebase/storage';

/**
 * Gets all the documents in animatedIllustrationNameComponentCategories collection
 *
 * @export
 * @return {Promise} - Promise which succeeds once the request is successful
 */
export async function getAnimatedIllustrationNameComponentCategories() {
  var db = firebase.firestore();
  return await db
    .collection('animatedIllustrationNameComponentCategories')
    .get();
}

/**
 * Gets the details of the animatedIllustration document with uid as animatedIllustrationUid
 *
 * @export
 * @param {String} animatedIllustrationUid - UID of the animatedIllustration document
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the document data object.
 */
export async function getAnimatedIllustrationDetails(animatedIllustrationUid) {
  if (animatedIllustrationUid) {
    var db = firebase.firestore();
    var doc = await db
      .collection('animated_illustrations')
      .doc(animatedIllustrationUid)
      .get();
    if (doc.exists) {
      return {
        ...doc.data(),
        id: doc.id,
      };
    } else {
      throw new Error('AnimatedIllustration with this ID doesnt exist');
    }
  } else {
    throw new Error('Invalid AnimatedIllustration ID');
  }
}

/**
 * Creates a new AnimatedIllustration
 * @export
 * @param {Object} name - Name object of the AnimatedIllustration
 * @param {File} animatedIllustrationFile - Svg file of the AnimatedIllustration
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the newly created animatedIllustration's uid.
 */
export async function createAnimatedIllustration(
  name,
  animatedIllustrationFile,
  userId
) {
  var combinedName = getCombinedName(name);
  console.log(combinedName);
  // Check if AnimatedIllustration name is unique
  await isAnimatedIllustrationNameUnique(combinedName);
  console.log('isAnimatedIllustrationNameUnique success');
  // Create AnimatedIllustration record on firebase
  var recordId = await createAnimatedIllustrationRecordOnDb(
    name,
    combinedName,
    userId
  );
  console.log('createAnimatedIllustrationRecordOnDb success');
  // upload image to firebase storage
  var fileUrl = await uploadAnimatedIllustrationFile(
    combinedName,
    animatedIllustrationFile
  );
  // Add url to uploaded image to created record
  await addFileUrlForAnimatedIllustration(recordId, userId, fileUrl);
  return recordId;
}

/**
 * Updates an already created animatedIllustration
 *
 * @export
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} animatedIllustrationId - UID of the animatedIllustration document
 * @param {File} animatedIllustrationFile - Svg file of the animatedIllustration
 * @param {String} combinedName - Combined name of the animatedIllustration. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} Promise which succeeds once the request is successful.
 */
export async function updateAnimatedIllustration(
  userId,
  animatedIllustrationId,
  animatedIllustrationFile,
  combinedName
) {
  // upload image to firebase storage
  var fileUrl = await uploadAnimatedIllustrationFile(
    combinedName,
    animatedIllustrationFile
  );
  // Update url of uploaded image to created record
  return await addFileUrlForAnimatedIllustration(
    animatedIllustrationId,
    userId,
    fileUrl
  );
}

/**
 * Returns the combined animatedIllustration name given the animatedIllustration Name object
 *
 * @export
 * @param {Object} name - Name object of the animatedIllustration. Should contain 3 keys : product_category, animated_illustration_category, animated_illustration_name
 * @return {String} - The combined name string of the AnimatedIllustration
 */
export function getCombinedName(name) {
  return `${name.product_category}_${name.animated_illustration_category}_${name.animated_illustration_name}`;
}

/**
 * Checks if the AnimatedIllustration with the given name already exists or not
 *
 * @param {String} combinedName - Combined name of the AnimatedIllustration. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} - Promise which succeeds if the AnimatedIllustration name is unique else throws an error
 */
async function isAnimatedIllustrationNameUnique(combinedName) {
  // check on firebase if animatedIllustration with same name already exists
  var db = firebase.firestore();
  var querySnapshot = await db
    .collection('animated_illustrations')
    .where('combined_name', '==', combinedName)
    .limit(1)
    .get();
  if (querySnapshot.size > 0) {
    throw new Error('AnimatedIllustration with the same name already exists');
  } else {
    return;
  }
}

/**
 * Created document for an animatedIllustration on the database
 *
 * @param {Object} name - Name object of the animatedIllustration
 * @param {String} combinedName - Combined name of the animatedIllustration. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds when the animatedIllustration is successfully created, else throws an error.
 */
async function createAnimatedIllustrationRecordOnDb(
  name,
  combinedName,
  userId
) {
  var db = firebase.firestore();
  var animatedIllustrationsCollectionRef = db.collection(
    'animated_illustrations'
  );

  var animatedIllustrationObj = {
    name: name,
    combined_name: combinedName,
    created_at: firebase.firestore.FieldValue.serverTimestamp(),
    created_by: userId,
    last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    last_updated_by: userId,
  };

  const docRef = await animatedIllustrationsCollectionRef.add(
    animatedIllustrationObj
  );
  console.log('Animated Illustrtion record creation success' + docRef.id);
  return docRef.id;
}

/**
 * Updates animatedIllustration document's image_url field
 *
 * @param {String} recordId - UID of the animatedIllustration document
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} fileUrl - Url of the uploaded file
 * @return {Promise} - Promise that succeeds when the updation of the field is successful.
 */
async function addFileUrlForAnimatedIllustration(recordId, userId, fileUrl) {
  var db = firebase.firestore();
  var animatedIllustrationsCollectionRef = db.collection(
    'animated_illustrations'
  );

  return await animatedIllustrationsCollectionRef.doc(recordId).update({
    img_url: fileUrl,
    last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    last_updated_by: userId,
  });
}

/**
 * Uploads an animatedIllustration file to storage and returns url of the uploaded file
 *
 * @param {String} combinedAnimatedIllustrationName - Combined name of the animatedIllustration. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {File} file - Svg file of the animatedIllustration
 * @return {Promise} - Promise that succeeds once the uploading of the animatedIllustration file is successful. The promise contains the download url of the file.
 */
async function uploadAnimatedIllustrationFile(
  combinedAnimatedIllustrationName,
  file
) {
  // Identifier could be default or region identifier or event identifier
  const storage = firebase.storage();
  var animatedIllustrationsRootFolderRef = storage.ref(
    'coffer/animated_illustrations'
  );
  var animatedIllustrationImageRef = animatedIllustrationsRootFolderRef.child(
    `${combinedAnimatedIllustrationName}.json`
  );
  const maxAge = 90 * 24 * 60 * 60; // days * hours * minutes * seconds
  var metadata = {
    cacheControl: `max-age=${maxAge}`,
  };
  await animatedIllustrationImageRef.put(file, metadata);
  const url = await animatedIllustrationImageRef.getDownloadURL();
  return url;
}
